/**
 * Performance dashboard figures: monthly KPI snapshots rolled up into the period a
 * person picks (month, quarter, year to date, trailing twelve months), compared with
 * the budget, the prior period and the same period a year earlier.
 *
 * Every KPI for a rolled-up period is computed by the formula library from summed
 * flows and the closing month's balances, never by averaging monthly KPIs.
 */
import type { Cents } from "../money";
import { variance, type Variance } from "./budget";
import { Exact } from "./exact";
import { computeKpis, displayKpi, operatingExpensesOf, runwayReading, type BalanceFigures, type KpiInputs, type KpiKey, type KpiKind, type KpiResult } from "./kpis";
import { lightFor, type Light, type Target } from "./scorecard";

export type DashView = "month" | "quarter" | "ytd" | "ttm";
export const DASH_VIEWS: DashView[] = ["month", "quarter", "ytd", "ttm"];
export type DashCompare = "budget" | "prior_period" | "prior_year";

/** One stored monthly snapshot. */
export type MonthActual = { period: string; quality: "final" | "preliminary"; inputs: KpiInputs };
/** One month of an approved (or draft) budget, by kind. Costs are positive. */
export type BudgetMonth = { revenue: Cents; cogs: Cents; operatingExpenses: Cents; otherNet: Cents };

export type Flows = {
  revenue: Cents;
  cogs: Cents;
  grossProfit: Cents;
  operatingExpenses: Cents;
  operatingIncome: Cents;
  /** Other income − other expenses. */
  otherNet: Cents;
  netIncome: Cents;
};

export type StoredKpi = { display: string; exact: string | null; note: string | null };

export type DashPoint = {
  /** "2026-09", "2026-Q3", "2026-09" (YTD and TTM are keyed by their last month). */
  key: string;
  label: string;
  /** The months the point covers, oldest first. */
  months: string[];
  /** Last month in the point; balances are as of its end. */
  end: string;
  /** False for a quarter still in progress (shown "to date"). */
  complete: boolean;
  /** True when any month in it is not closed yet. */
  preliminary: boolean;
  flows: Flows;
  balance: BalanceFigures;
  kpis: Record<KpiKey, StoredKpi>;
  /** Budget for the same months, when the budget covers every one of them. */
  budget: (Flows & { grossMargin: StoredKpi; netMargin: StoredKpi }) | null;
  /** Same months a year earlier, when every one of them has a snapshot. */
  priorYear: Flows | null;
};

export function shiftPeriod(period: string, by: number): string {
  const [y, m] = period.split("-").map(Number) as [number, number];
  const idx = y * 12 + (m - 1) + by;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
}

const daysIn = (period: string) => {
  const [y, m] = period.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function periodLabel(period: string): string {
  const [y, m] = period.split("-").map(Number) as [number, number];
  return `${MONTHS[m - 1]} ${y}`;
}

const store = (r: KpiResult): StoredKpi => ({ display: r.display, exact: r.exact ? r.exact.toString() : null, note: r.note });

const nullableSum = (vals: Array<Cents | null>): Cents | null => (vals.some((v) => v === null) ? null : vals.reduce<number>((a, v) => a + v!, 0));

function flowsOf(i: KpiInputs): Flows {
  const p = i.period;
  return {
    revenue: p.revenue,
    cogs: p.cogs,
    grossProfit: p.revenue - p.cogs,
    // QuickBooks' own total when stored; the snapshot's tie-out checks it equals the derived figure.
    operatingExpenses: operatingExpensesOf(p),
    operatingIncome: p.operatingIncome,
    otherNet: p.netIncome - p.operatingIncome,
    netIncome: p.netIncome,
  };
}

function addFlows(list: Flows[]): Flows {
  const sum = (k: keyof Flows) => list.reduce((a, f) => a + f[k], 0);
  return { revenue: sum("revenue"), cogs: sum("cogs"), grossProfit: sum("grossProfit"), operatingExpenses: sum("operatingExpenses"), operatingIncome: sum("operatingIncome"), otherNet: sum("otherNet"), netIncome: sum("netIncome") };
}

/** Summed flows over the months, with balances, burn and headcount from the last month. All months carry the same settings (applied on read). */
export function rollUp(months: MonthActual[]): KpiInputs {
  const last = months[months.length - 1]!;
  const ps = months.map((m) => m.inputs.period);
  return {
    period: {
      days: months.reduce((a, m) => a + daysIn(m.period), 0),
      revenue: ps.reduce((a, p) => a + p.revenue, 0),
      invoicedSales: nullableSum(ps.map((p) => p.invoicedSales)),
      cogs: ps.reduce((a, p) => a + p.cogs, 0),
      operatingIncome: ps.reduce((a, p) => a + p.operatingIncome, 0),
      ...(ps.every((p) => p.operatingExpenses !== undefined) ? { operatingExpenses: ps.reduce((a, p) => a + p.operatingExpenses!, 0) } : {}),
      netIncome: ps.reduce((a, p) => a + p.netIncome, 0),
      interestExpense: nullableSum(ps.map((p) => p.interestExpense)),
      interestIncome: nullableSum(ps.map((p) => p.interestIncome)),
      taxes: nullableSum(ps.map((p) => p.taxes)),
      depreciationAmortization: nullableSum(ps.map((p) => p.depreciationAmortization)),
      payrollCost: nullableSum(ps.map((p) => p.payrollCost)),
      payrollInOperatingCosts: nullableSum(ps.map((p) => p.payrollInOperatingCosts)),
      depreciationInOperatingCosts: nullableSum(ps.map((p) => p.depreciationInOperatingCosts)),
      fixedCosts: nullableSum(ps.map((p) => p.fixedCosts)),
      debtService: nullableSum(ps.map((p) => p.debtService)),
    },
    balance: last.inputs.balance,
    cashMonthEnds: last.inputs.cashMonthEnds,
    headcount: last.inputs.headcount,
    settings: last.inputs.settings,
  };
}

/**
 * KPIs over several months. Revenue per employee is a monthly figure, so over a quarter or a year it
 * is the average month's revenue ÷ headcount, not the summed revenue.
 */
export function rollUpKpis(months: MonthActual[]): Record<KpiKey, KpiResult> {
  const kpis = computeKpis(rollUp(months));
  const rpe = kpis.revenue_per_employee;
  if (months.length > 1 && rpe.exact) {
    const exact = rpe.exact.div(Exact.int(months.length));
    kpis.revenue_per_employee = { ...rpe, exact, display: displayKpi(rpe.kind, exact) };
  }
  return kpis;
}

function budgetFlows(b: BudgetMonth[]): Flows {
  const revenue = b.reduce((a, m) => a + m.revenue, 0);
  const cogs = b.reduce((a, m) => a + m.cogs, 0);
  const operatingExpenses = b.reduce((a, m) => a + m.operatingExpenses, 0);
  const otherNet = b.reduce((a, m) => a + m.otherNet, 0);
  const operatingIncome = revenue - cogs - operatingExpenses;
  return { revenue, cogs, grossProfit: revenue - cogs, operatingExpenses, operatingIncome, otherNet, netIncome: operatingIncome + otherNet };
}

const margin = (num: Cents, revenue: Cents): StoredKpi => {
  if (revenue <= 0) return { display: "—", exact: null, note: "No revenue budgeted" };
  const v = Exact.int(num).div(Exact.int(revenue));
  return { display: displayKpi("percent", v), exact: v.toString(), note: null };
};

/** The months each point of a view covers, ending at each month we have, newest last. */
/** The first month of the fiscal year containing `period` ("2026-09" with a July year → "2026-07"). */
export function fiscalYearStart(period: string, fyStartMonth: number): string {
  const [y, m] = period.split("-").map(Number) as [number, number];
  return `${m >= fyStartMonth ? y : y - 1}-${String(fyStartMonth).padStart(2, "0")}`;
}

/**
 * The fiscal quarter a month falls in. Quarters count from the fiscal year's first month; a
 * calendar-year client reads "Q3 2026", another "Q1 FY2027" (named for the year it ends in).
 * `position` is the month's place in the quarter (0–2).
 */
export function fiscalQuarter(period: string, fyStartMonth = 1): { key: string; label: string; first: string; position: number } {
  const m = Number(period.slice(5, 7));
  const offset = (m - fyStartMonth + 12) % 12;
  const n = Math.floor(offset / 3) + 1;
  const position = offset % 3;
  const fyStart = fiscalYearStart(period, fyStartMonth);
  const label = fyStartMonth === 1 ? `Q${n} ${period.slice(0, 4)}` : `Q${n} FY${Number(fyStart.slice(0, 4)) + 1}`;
  return { key: fyStartMonth === 1 ? `${period.slice(0, 4)}-Q${n}` : `FY${Number(fyStart.slice(0, 4)) + 1}-Q${n}`, label, first: shiftPeriod(period, -position), position };
}

function groupsFor(view: DashView, periods: string[], fyStartMonth: number): Array<{ key: string; label: string; months: string[]; complete: boolean }> {
  const have = new Set(periods);
  const out: Array<{ key: string; label: string; months: string[]; complete: boolean }> = [];
  if (view === "month") for (const p of periods) out.push({ key: p, label: periodLabel(p), months: [p], complete: true });
  if (view === "quarter") {
    const quarters = new Map<string, { label: string; first: string; months: string[] }>();
    for (const p of periods) {
      const q = fiscalQuarter(p, fyStartMonth);
      const cur = quarters.get(q.key) ?? { label: q.label, first: q.first, months: [] };
      cur.months.push(p);
      quarters.set(q.key, cur);
    }
    for (const [key, q] of quarters) {
      // A quarter missing an early month can't be compared honestly; one still in progress is shown "to date".
      const all = [0, 1, 2].map((k) => shiftPeriod(q.first, k));
      if (all.slice(0, q.months.length).join() !== q.months.join()) continue;
      const complete = q.months.length === 3;
      out.push({ key, label: complete ? q.label : `${q.label} to date`, months: q.months, complete });
    }
  }
  if (view === "ytd" || view === "ttm") {
    for (const p of periods) {
      const first = view === "ytd" ? fiscalYearStart(p, fyStartMonth) : shiftPeriod(p, -11);
      const months: string[] = [];
      for (let m = first; m <= p; m = shiftPeriod(m, 1)) months.push(m);
      // Year to date and trailing twelve months are shown only when every month is there.
      if (!months.every((m) => have.has(m))) continue;
      out.push({ key: p, label: view === "ytd" ? `YTD ${periodLabel(p)}` : `12 months to ${periodLabel(p)}`, months, complete: true });
    }
  }
  return out;
}

/** Every point of a view the snapshots allow, oldest first. Year to date follows the fiscal year (`fyStartMonth`, 1 = January). */
export function buildPoints(view: DashView, actuals: MonthActual[], budget: Map<string, BudgetMonth>, fyStartMonth = 1): DashPoint[] {
  const sorted = [...actuals].sort((a, b) => a.period.localeCompare(b.period));
  const byPeriod = new Map(sorted.map((a) => [a.period, a]));
  return groupsFor(
    view,
    sorted.map((a) => a.period),
    fyStartMonth,
  ).map((g) => {
      const months = g.months.map((m) => byPeriod.get(m)!);
      const inputs = rollUp(months);
      const kpis = rollUpKpis(months);
      const b = g.months.every((m) => budget.has(m)) ? budgetFlows(g.months.map((m) => budget.get(m)!)) : null;
      const prior = g.months.map((m) => byPeriod.get(shiftPeriod(m, -12)));
      return {
        key: g.key,
        label: g.label,
        months: g.months,
        end: g.months[g.months.length - 1]!,
        complete: g.complete,
        preliminary: months.some((m) => m.quality === "preliminary"),
        flows: flowsOf(inputs),
        balance: inputs.balance,
        kpis: Object.fromEntries(Object.entries(kpis).map(([k, v]) => [k, store(v)])) as Record<KpiKey, StoredKpi>,
        budget: b ? { ...b, grossMargin: margin(b.grossProfit, b.revenue), netMargin: margin(b.netIncome, b.revenue) } : null,
        priorYear: prior.every(Boolean) ? addFlows(prior.map((m) => flowsOf(m!.inputs))) : null,
      };
    });
}

// --- Headline tiles ----------------------------------------------------------------------------

export type TileKey = "revenue" | "gross_margin" | "net_income" | "cash" | "runway" | "dso";
export const TILE_KEYS: TileKey[] = ["revenue", "gross_margin", "net_income", "cash", "runway", "dso"];

export type Comparison = { label: string; display: string; favorable: boolean | null };

export type Tile = {
  key: TileKey;
  label: string;
  kind: KpiKind;
  display: string;
  note: string | null;
  /** Value at each point of the view, for the trend line (null where not meaningful). */
  trend: Array<{ key: string; value: number | null }>;
  comparison: Comparison | null;
  light: Light;
  target: string | null;
};

const TILE_DEF: Record<TileKey, { label: string; kind: KpiKind; higherIsBetter: boolean; kpi: KpiKey | null }> = {
  revenue: { label: "Revenue", kind: "money", higherIsBetter: true, kpi: null },
  gross_margin: { label: "Gross margin", kind: "percent", higherIsBetter: true, kpi: "gross_margin" },
  net_income: { label: "Net income", kind: "money", higherIsBetter: true, kpi: null },
  cash: { label: "Cash", kind: "money", higherIsBetter: true, kpi: null },
  runway: { label: "Runway at current burn", kind: "months", higherIsBetter: true, kpi: "runway" },
  dso: { label: "Days sales outstanding", kind: "days", higherIsBetter: false, kpi: "dso" },
};

function tileValue(key: TileKey, p: DashPoint): { exact: Exact | null; note: string | null } {
  if (key === "revenue") return { exact: Exact.int(p.flows.revenue), note: null };
  if (key === "net_income") return { exact: Exact.int(p.flows.netIncome), note: null };
  if (key === "cash") return { exact: Exact.int(p.balance.cash), note: null };
  const k = p.kpis[TILE_DEF[key].kpi!];
  return { exact: k.exact ? Exact.parse(k.exact) : null, note: k.note };
}

function budgetValue(key: TileKey, p: DashPoint): Exact | null {
  if (!p.budget) return null;
  if (key === "revenue") return Exact.int(p.budget.revenue);
  if (key === "net_income") return Exact.int(p.budget.netIncome);
  if (key === "gross_margin") return p.budget.grossMargin.exact ? Exact.parse(p.budget.grossMargin.exact) : null;
  return null;
}

/**
 * How the current value compares: money as a percent change (or dollars when the base
 * is zero or negative), percentages in points, days and months as a difference.
 */
export function compareValues(kind: KpiKind, current: Exact, base: Exact, higherIsBetter: boolean, label: string): Comparison {
  const diff = current.sub(base);
  let magnitude: string;
  if (kind === "money") magnitude = base.sign() > 0 ? displayKpi("percent", diff.abs().div(base)) : displayKpi("money", diff.abs());
  else if (kind === "percent") magnitude = `${diff.abs().mul(Exact.int(100)).toFixed(1)} pts`;
  else magnitude = displayKpi(kind, diff.abs());
  // A change too small to show (it rounds to zero) is no change, not a favorable or unfavorable one.
  if (!/[1-9]/.test(magnitude)) return { label, display: magnitude, favorable: null };
  const sign = diff.sign() > 0 ? "+" : "−";
  return { label, display: `${sign}${magnitude}`, favorable: (diff.sign() > 0) === higherIsBetter };
}

/**
 * The six headline figures for the selected point, compared as asked, with the trend
 * over the twelve points ending there. Pass every point (not just those shown) so a
 * prior-year comparison can find the point a year earlier.
 */
export function buildTiles(points: DashPoint[], selected: string, compare: DashCompare, targets: Partial<Record<KpiKey, Target>>, view: DashView): Tile[] {
  const idx = points.findIndex((p) => p.key === selected);
  if (idx < 0) return [];
  const p = points[idx]!;
  const prevLabel = { month: "vs prior month", quarter: "vs prior quarter", ytd: "", ttm: "vs prior month" }[view];
  return TILE_KEYS.map((key) => {
    const def = TILE_DEF[key];
    const cur = tileValue(key, p);
    let base: Exact | null = null;
    let label = "";
    if (compare === "budget") {
      base = budgetValue(key, p);
      label = "vs budget";
    } else if (compare === "prior_period") {
      // Year to date grows every month, so it is compared with the prior year only. A quarter still
      // in progress isn't compared with a whole one. The prior point is found by its dates, so a
      // missing month never pairs a month with the wrong one. The trailing twelve months roll by one
      // month, so they are compared with the twelve months ending a month earlier.
      const prev =
        view === "ytd" || (view === "quarter" && !p.complete)
          ? undefined
          : points.find((x) => x.end === shiftPeriod(view === "ttm" ? p.end : p.months[0]!, -1) && (view === "quarter" ? x.complete : x.months.length === p.months.length));
      base = prev ? tileValue(key, prev).exact : null;
      label = prevLabel;
    } else {
      label = "vs prior year";
      if (p.priorYear && (key === "revenue" || key === "net_income" || key === "gross_margin")) {
        const py = p.priorYear;
        base = key === "revenue" ? Exact.int(py.revenue) : key === "net_income" ? Exact.int(py.netIncome) : py.revenue > 0 ? Exact.int(py.grossProfit).div(Exact.int(py.revenue)) : null;
      } else if (key === "cash" || key === "runway" || key === "dso") {
        const year = points.find((x) => x.end === shiftPeriod(p.end, -12) && x.months.length === p.months.length);
        base = year ? tileValue(key, year).exact : null;
      }
    }
    const t = def.kpi ? targets[def.kpi] : undefined;
    // Runway has no limit while cash isn't going down; the library's reading says so instead of showing a dash.
    const reading = key === "runway" ? runwayReading(p.kpis.runway, p.kpis.net_burn) : null;
    return {
      key,
      label: def.label,
      kind: def.kind,
      display: reading ? reading.display : displayKpi(def.kind, cur.exact),
      note: reading ? reading.note : cur.note,
      trend: points.slice(Math.max(0, idx - 11), idx + 1).map((x) => {
        const v = tileValue(key, x).exact;
        return { key: x.key, value: v ? Number(v.toFixed(def.kind === "money" ? 0 : 4)) : null };
      }),
      comparison: cur.exact && base ? compareValues(def.kind, cur.exact, base, def.higherIsBetter, label) : null,
      light: t ? lightFor(cur.exact, t) : "none",
      target: t ? `${t.comparator === "at_least" ? "Target at least" : "Target at most"} ${displayKpi(def.kind, Exact.decimal(t.target))}` : null,
    };
  });
}

// --- Budget variance and revenue breakdown -----------------------------------------------------

export type VarianceRow = { key: "revenue" | "cogs" | "operating_expenses" | "other" | "net_income"; label: string } & Variance;

/** Actual vs budget by category for one point; null when the budget doesn't cover it. */
export function varianceRows(p: DashPoint): VarianceRow[] | null {
  if (!p.budget) return null;
  const b = p.budget;
  const f = p.flows;
  return [
    { key: "revenue", label: "Revenue", ...variance("income", b.revenue, f.revenue) },
    { key: "cogs", label: "Cost of goods sold", ...variance("cogs", b.cogs, f.cogs) },
    { key: "operating_expenses", label: "Operating expenses", ...variance("expense", b.operatingExpenses, f.operatingExpenses) },
    { key: "other", label: "Other income and expenses", ...variance("other_income", b.otherNet, f.otherNet) },
    { key: "net_income", label: "Net income", ...variance("net_income", b.netIncome, f.netIncome) },
  ];
}

/**
 * Where each dollar of revenue went: cost of goods, operating expenses, other items
 * and what was left as profit, as shares of revenue (exact text and display).
 * Null when there was no revenue.
 */
export function revenueBreakdown(f: Flows): Array<{ key: string; label: string; cents: Cents; share: string; display: string }> | null {
  if (f.revenue <= 0) return null;
  const parts = [
    { key: "cogs", label: "Cost of goods sold", cents: f.cogs },
    { key: "operating_expenses", label: "Operating expenses", cents: f.operatingExpenses },
    { key: "other", label: "Other expenses, net", cents: -f.otherNet },
    { key: "profit", label: f.netIncome >= 0 ? "Net profit" : "Net loss", cents: f.netIncome },
  ];
  return parts.map((x) => {
    const share = Exact.int(x.cents).div(Exact.int(f.revenue));
    return { ...x, share: share.toString(), display: displayKpi("percent", share) };
  });
}
